import React, { FC, useState } from 'react';
import { FormControl, Text, TextFieldAppearance, TextFieldSize, Combobox } from '@bechtel/gds';
import { NetworkStatus } from '@apollo/client';
import { Field, FormikProps } from 'formik';
import { get } from 'lodash';
import { ErrorRetry, useDebounce } from '@bechtel/client-common';
import { useCompanyQuery } from '~/user-admin/hooks/use-company';
import { Company } from '~/user-admin/users/list/drawer/company-list/types';
import { FormikState } from '~/user-admin/users/form-state';
import { t, Trans } from '@lingui/macro';

const Item = ({ item }: any) => {
  return (
    <Text size="300" color="grey.1000">
      {item?.name}
    </Text>
  );
};

const SelectedItem = ({ item }: any) => {
  return (
    <Text size="300" color="grey.1000" family="graphik">
      {item?.name ? item.name : t`Please Select`}
    </Text>
  );
};

interface CompanyListProps {
  formikProps: FormikProps<FormikState>;
}

export const CompanyList: FC<CompanyListProps> = ({ formikProps }) => {
  const { setFieldValue, values } = formikProps;
  const [searchKeyword, setSearchKeyword] = useState('');
  const debouncedSearchText = useDebounce(searchKeyword, 500);
  const itemToString = (item: Company) => item?.name;

  const {
    loading: companyLoading,
    error: companyError,
    data: companyData,
    refetch: companyRefetch,
    networkStatus: companyNetworkStatus,
  } = useCompanyQuery({ searchKeyword: debouncedSearchText }, { cursor: '1', limit: 20 });
  const companyList: Company[] = get(companyData, 'loadCompanies', []);

  return (
    <>
      {!companyLoading && companyError && (
        <ErrorRetry refetch={companyRefetch}>
          <Trans>There was a problem loading company list. Please click the button below to try again.</Trans>
        </ErrorRetry>
      )}
      {!companyError && (
        <Field name="company">
          {({ field }) => {
            return (
              <FormControl style={{ flexGrow: 1 }} label={t`Company`}>
                <Combobox
                  {...field}
                  ariaLabel="selectCompany"
                  items={companyList}
                  itemComponent={Item}
                  selectedItem={values?.company ? values.company : t`Please Select`}
                  selectedComponent={SelectedItem}
                  term={searchKeyword}
                  setTerm={setSearchKeyword}
                  itemString={itemToString}
                  restorePreviousOnSkip
                  size={TextFieldSize.LARGE}
                  appearance={TextFieldAppearance.NORMAL}
                  onSelect={async (selectedItem: any) => {
                    setFieldValue('company', selectedItem);
                  }}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchKeyword(event.target.value);
                  }}
                  loading={companyLoading || companyNetworkStatus === NetworkStatus.refetch}
                  messageOnNoResults={t`No results found`}
                />
              </FormControl>
            );
          }}
        </Field>
      )}
    </>
  );
};

export default CompanyList;
